// Fluxo da atualização (feature atualizacao):
//  - verifica ao abrir e a cada 6 h, em silêncio (sem internet ou já atualizado: nada aparece) — AC-200
//  - achou versão nova: pergunta antes de baixar, com as novidades — AC-201 / ASM-091
//  - aceitou: baixa (com progresso), instala e reabre; falhou: avisa e segue na versão atual — AC-202
//  - "Agora não": não pergunta de novo enquanto o app estiver aberto — AC-203
import type { AvailableUpdate, UpdaterBridge } from '../platform/updater';

export const CHECK_EVERY_MS = 6 * 60 * 60 * 1000;

export type UpdatePhase = 'idle' | 'available' | 'downloading' | 'installing' | 'error';

export interface UpdateState {
  phase: UpdatePhase;
  update: AvailableUpdate | null;
  progress: number | null;
}

const IDLE: UpdateState = { phase: 'idle', update: null, progress: null };

export function createUpdateController(bridge: UpdaterBridge | null) {
  let state: UpdateState = IDLE;
  let dismissed = false;
  let checking = false;
  const listeners = new Set<() => void>();
  const set = (next: UpdateState) => {
    state = next;
    listeners.forEach((l) => l());
  };

  async function check() {
    if (!bridge || dismissed || checking || state.phase !== 'idle') return;
    checking = true;
    try {
      const update = await bridge.check();
      if (update && !dismissed) set({ phase: 'available', update, progress: null });
    } catch {
      /* sem internet ou servidor fora: tenta de novo na próxima verificação */
    } finally {
      checking = false;
    }
  }

  return {
    get: () => state,
    subscribe(l: () => void) {
      listeners.add(l);
      return () => listeners.delete(l);
    },
    check,
    /** Verifica agora e a cada 6 h; devolve a função que para. */
    start(intervalMs = CHECK_EVERY_MS) {
      if (!bridge) return () => {};
      void check();
      const id = setInterval(() => void check(), intervalMs);
      return () => clearInterval(id);
    },
    later() {
      dismissed = true;
      set(IDLE);
    },
    async install() {
      const update = state.update;
      if (!bridge || !update || state.phase !== 'available') return;
      set({ phase: 'downloading', update, progress: 0 });
      try {
        await update.install((progress) => set({ phase: 'downloading', update, progress }));
        set({ phase: 'installing', update, progress: 100 });
        await bridge.relaunch();
      } catch (err) {
        // assinatura inválida, download interrompido, instalador recusado… (AC-202 / AC-204)
        // o motivo fica no console (DevTools) para diagnóstico; a tela mostra o aviso amigável
        console.error('[atualização] falhou:', err);
        set({ phase: 'error', update, progress: null });
      }
    },
    closeError() {
      dismissed = true;
      set(IDLE);
    },
  };
}

export type UpdateController = ReturnType<typeof createUpdateController>;
